import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { ChevronDown, Menu, X, LogIn, LayoutGrid, ArrowUpRight, ArrowRight } from 'lucide-react';
import { MEGA_MENUS, UTILITY_LINKS, type MegaMenu, type MenuLink } from './navigation';
import { useSession } from '@/lib/session';
import { orderLink } from '@/lib/site';

const isExternal = (to: string) => /^https?:/.test(to);

function SmartLink({ to, className, children, onClick }: { to: string; className?: string; children: React.ReactNode; onClick?: () => void }) {
  if (isExternal(to)) {
    return (
      <a href={to} target="_blank" rel="noopener noreferrer" className={className} onClick={onClick}>
        {children}
      </a>
    );
  }
  return (
    <Link to={to} className={className} onClick={onClick}>
      {children}
    </Link>
  );
}

function UtilityBar() {
  const { session } = useSession();
  return (
    <div className="hidden md:block bg-navy-900 text-slate-300 text-xs">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-9 flex items-center justify-between">
        <span className="flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-status-running" />
          Industrial monitoring that runs without the internet
        </span>
        <nav className="flex items-center gap-5">
          {UTILITY_LINKS.map((l) => (
            <SmartLink key={l.label} to={l.to} className="hover:text-white transition-colors inline-flex items-center gap-1">
              {l.label}
              {isExternal(l.to) && <ArrowUpRight size={11} />}
            </SmartLink>
          ))}
          <span className="h-3.5 w-px bg-white/15" />
          <Link to={session ? '/portal' : '/login'} className="font-semibold text-white hover:text-brand-accent transition-colors">
            {session ? 'Customer Portal' : 'Customer Login'}
          </Link>
        </nav>
      </div>
    </div>
  );
}

function MenuItemLink({ link, onNavigate }: { link: MenuLink; onNavigate: () => void }) {
  const Icon = link.icon;
  return (
    <SmartLink to={link.to} onClick={onNavigate} className="group flex items-start gap-3 rounded-[var(--radius-glass-sm)] p-2.5 -mx-2.5 hover:bg-ink-muted transition-colors">
      {Icon && (
        <span
          className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-transform group-hover:scale-110"
          style={{ backgroundColor: `${link.color}14`, color: link.color }}
        >
          <Icon size={16} />
        </span>
      )}
      <span className="min-w-0">
        <span className="block text-sm font-semibold text-text-primary group-hover:text-brand-primary transition-colors">{link.label}</span>
        {link.description && <span className="block text-xs text-text-secondary mt-0.5 leading-snug">{link.description}</span>}
      </span>
    </SmartLink>
  );
}

function MegaPanel({ menu, onNavigate }: { menu: MegaMenu; onNavigate: () => void }) {
  const wide = menu.columns.length > 1;
  return (
    <div className="absolute left-0 right-0 top-full pt-2 animate-menu-in">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-[var(--radius-glass)] border border-glass-border shadow-2xl shadow-navy-900/10 overflow-hidden">
          <div className={`grid ${menu.feature ? (wide ? 'lg:grid-cols-[1fr_1fr_320px]' : 'lg:grid-cols-[1fr_320px]') : wide ? 'lg:grid-cols-2' : ''}`}>
            {menu.columns.map((col, i) => (
              <div key={i} className="p-6">
                <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-text-muted mb-3 h-4">{col.title.trim()}</div>
                <div className={`grid gap-1 ${!wide && col.links.length > 4 ? 'sm:grid-cols-2 gap-x-6' : ''}`}>
                  {col.links.map((link) => (
                    <MenuItemLink key={link.label} link={link} onNavigate={onNavigate} />
                  ))}
                </div>
              </div>
            ))}
            {menu.feature && (
              <div className="relative p-6 bg-navy-900 text-white overflow-hidden">
                <div className="absolute -right-16 -top-16 w-56 h-56 rounded-full bg-brand-secondary/30 blur-3xl" />
                <div className="absolute -left-10 bottom-0 w-40 h-40 rounded-full bg-brand-primary/30 blur-3xl" />
                <div className="relative">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-brand-accent">{menu.feature.eyebrow}</div>
                  <div className="mt-3 text-lg font-bold leading-snug">{menu.feature.title}</div>
                  <p className="mt-2 text-sm text-slate-300 leading-relaxed">{menu.feature.body}</p>
                  <SmartLink to={menu.feature.to} onClick={onNavigate} className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-white hover:gap-2.5 transition-all">
                    {menu.feature.cta} <ArrowRight size={14} />
                  </SmartLink>
                </div>
              </div>
            )}
          </div>
          <div className="px-6 py-3 bg-ink-muted/60 border-t border-glass-border flex items-center justify-between text-xs">
            <Link to={menu.to} onClick={onNavigate} className="font-semibold text-brand-primary inline-flex items-center gap-1 hover:gap-2 transition-all">
              Explore {menu.label.toLowerCase()} <ArrowRight size={12} />
            </Link>
            <Link to={orderLink('demo')} onClick={onNavigate} className="text-text-secondary hover:text-text-primary">
              Prefer a walkthrough? <span className="font-semibold text-text-primary">Book a demo</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export function SiteHeader() {
  const { session } = useSession();
  const location = useLocation();
  const [open, setOpen] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSection, setMobileSection] = useState<string | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const closeTimer = useRef<number | undefined>(undefined);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(null);
    setMobileOpen(false);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(null);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const openMenu = (label: string) => {
    window.clearTimeout(closeTimer.current);
    setOpen(label);
  };
  const scheduleClose = () => {
    window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setOpen(null), 120);
  };

  const active = MEGA_MENUS.find((m) => m.label === open);

  return (
    <header className="sticky top-0 z-50">
      <UtilityBar />
      <div className={`relative bg-white/95 backdrop-blur-md border-b transition-shadow ${scrolled ? 'border-glass-border shadow-sm' : 'border-transparent'}`} onMouseLeave={scheduleClose}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-[72px] gap-6">
            <Link to="/" className="shrink-0" aria-label="Scada.Ink home">
              <img src="/scada-ink-logo.png" alt="Scada.Ink" className="h-14 w-auto" />
            </Link>

            <nav className="hidden lg:flex items-center gap-1 h-full" aria-label="Main">
              {MEGA_MENUS.map((menu) => (
                <div key={menu.label} className="h-full flex items-center" onMouseEnter={() => openMenu(menu.label)}>
                  <button
                    type="button"
                    aria-expanded={open === menu.label}
                    onClick={() => setOpen(open === menu.label ? null : menu.label)}
                    className={`inline-flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      open === menu.label || location.pathname.startsWith(menu.to) ? 'text-brand-primary' : 'text-text-primary hover:text-brand-primary'
                    }`}
                  >
                    {menu.label}
                    <ChevronDown size={14} className={`transition-transform ${open === menu.label ? 'rotate-180' : ''}`} />
                  </button>
                </div>
              ))}
              <NavLink
                to="/pricing"
                onMouseEnter={scheduleClose}
                className={({ isActive }) => `px-3 py-2 rounded-lg text-sm font-medium transition-colors ${isActive ? 'text-brand-primary' : 'text-text-primary hover:text-brand-primary'}`}
              >
                Pricing
              </NavLink>
            </nav>

            <div className="hidden lg:flex items-center gap-2" onMouseEnter={scheduleClose}>
              <Link
                to={session ? '/portal' : '/login'}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-semibold text-text-primary hover:text-brand-primary transition-colors"
              >
                {session ? <LayoutGrid size={15} /> : <LogIn size={15} />}
                {session ? 'Portal' : 'Log in'}
              </Link>
              <Link
                to={orderLink('quote')}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-[var(--radius-glass-sm)] bg-gradient-to-r from-brand-primary to-brand-secondary text-white text-sm font-semibold shadow-md shadow-brand-primary/20 hover:brightness-110 transition-all"
              >
                Get a Quote
              </Link>
            </div>

            <button
              type="button"
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden p-2 -mr-2 text-text-primary"
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileOpen}
            >
              {mobileOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {active && (
          <div className="hidden lg:block" onMouseEnter={() => openMenu(active.label)}>
            <MegaPanel menu={active} onNavigate={() => setOpen(null)} />
          </div>
        )}

        {mobileOpen && (
          <div className="lg:hidden border-t border-glass-border bg-white max-h-[calc(100vh-72px)] overflow-y-auto">
            <div className="px-4 py-3">
              {MEGA_MENUS.map((menu) => (
                <div key={menu.label} className="border-b border-glass-border">
                  <button
                    type="button"
                    onClick={() => setMobileSection(mobileSection === menu.label ? null : menu.label)}
                    className="w-full flex items-center justify-between py-3.5 text-base font-semibold text-text-primary"
                    aria-expanded={mobileSection === menu.label}
                  >
                    {menu.label}
                    <ChevronDown size={18} className={`transition-transform ${mobileSection === menu.label ? 'rotate-180' : ''}`} />
                  </button>
                  {mobileSection === menu.label && (
                    <div className="pb-3 pl-1 space-y-0.5">
                      <Link to={menu.to} className="block py-2 text-sm font-semibold text-brand-primary">
                        Overview
                      </Link>
                      {menu.columns.flatMap((c) => c.links).map((link) => (
                        <SmartLink key={link.label} to={link.to} className="block py-2 text-sm text-text-secondary">
                          {link.label}
                        </SmartLink>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <Link to="/pricing" className="block py-3.5 border-b border-glass-border text-base font-semibold text-text-primary">
                Pricing
              </Link>
              {UTILITY_LINKS.map((l) => (
                <SmartLink key={l.label} to={l.to} className="block py-2.5 text-sm text-text-secondary">
                  {l.label}
                </SmartLink>
              ))}
              <div className="grid grid-cols-2 gap-3 pt-4 pb-2">
                <Link to={session ? '/portal' : '/login'} className="text-center px-4 py-3 rounded-[var(--radius-glass-sm)] border border-glass-border-bright text-sm font-semibold">
                  {session ? 'Portal' : 'Log in'}
                </Link>
                <Link to={orderLink('quote')} className="text-center px-4 py-3 rounded-[var(--radius-glass-sm)] bg-gradient-to-r from-brand-primary to-brand-secondary text-white text-sm font-semibold">
                  Get a Quote
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
